import { useMemo, useRef } from 'react';

import { TradeDetailListSection, TradeText } from './styles';

import EmptyMessage from '@/app/components/EmptyMessage';
import { useTradeModalStore } from '@/app/hooks/trade/modalStore.tsx';
import { Text } from '@/styles/reactnative.tsx';
import { AuthCheckText } from "@app/components";
import { COLORS } from '@app/constants';
import { useToken } from "@app/hooks";
import { useFetcher } from "@app/hooks/fetcher";
import EntryTime from '@components/EntryTime.tsx';
import KrwPrice from '@components/KrwPrice.tsx';
import { Modal, Table } from 'antd';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import ShareModalContent from './ShareModalContent';
import TradeDetailError from "./TradeDetailError";
import TradeDetailListItem from "./TradeDetailListItem";
import useScrollLayout from '../useScrollLayout';
import { useExchangeStore } from '@/app/hooks/trade/exchangeStore';
import ContractsColumn from '../Columns/ContractsColumn';
import dayjs from 'dayjs';

const TradeFilledTable = ({parentHeight}) => {
  const { data: token } = useToken();
  const { data: tradeModalStore, mutate: muateTradeModalStore } =
		useTradeModalStore();

  const {t} = useTranslation();
  const { EPs } = useExchangeStore();

  const KEYURL = "/trade/orderlist?status=2&ep_id=*";

  const { data, isLoading, isError } = useFetcher(KEYURL, false);

  const list = data?.result?.result?.list;
  const containerRef = useRef<HTMLDivElement>(null);
  const { needsScroll, headerHeight } = useScrollLayout(containerRef, list, parentHeight);


  const dataType = [
    "contracts",
    "amount",
    "filledPrice",
    "orderPrice",
    "orderType",
    "margin",
    "fee",
    "orderTime",
  ];

  const renderItem = (item: any, index: any) => {
    return (
      <TradeDetailListItem
        item={item}
        index={index}
        dataType={dataType.length}
      />
    );
  };


  const columns = [
    {
      width: '10%',
      dataIndex: '',
      title: t('contracts'),
      render: (value, data) => (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
          <ContractsColumn item={data} />
        </div>
      )
    },
    {
      width: '10%',
      dataIndex: '',
      title: t('amount'),
      render: (value, data) => {
        return (
          <TradeText style={{ justifyContent: 'flex-start' }}>
            {BigNumber(data?.amount).multipliedBy(data?.leverage).toFormat(3)}
          </TradeText>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: t('orderPrice'),
      render: (value, data) => {
        const EP = EPs?.find((ep: any) => +ep.id === +data?.exchange_pairs_id);
        const currency = data?.pairs?.split("/")[1];
        const dp = EP?.dp || 3;

        return (
          <TradeText style={{whiteSpace: 'nowrap', justifyContent: 'flex-start'}}>
            {data?.order_price === 999999999 || data?.order_price === 0 ?
              "-" :
              `${BigNumber(data?.order_price).toFormat(dp)} ${currency}`
            }
          </TradeText>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: t('conclusionPrice'),
      render: (value, data) => {
        const EP = EPs?.find((ep: any) => +ep.id === +data?.exchange_pairs_id);
        const currency = data?.pairs?.split("/")[1];
        const dp = EP?.dp || 3;
        const price = data?.trade_price || data?.order_price;

        return (
          <TradeText style={{whiteSpace: 'nowrap', justifyContent: 'flex-start'}}>
            {BigNumber(price).toFormat(dp)} {currency}
          </TradeText>
        );
      },
    },
    // {
    //   dataIndex: '',
    //   title: t('orderPrice'),
    //   render: (value, data) => {
    //     return (
    //      <></>
    //     )
    //   },
    // },
    {
      width: '10%',
      dataIndex: '',
      title: t('orderType'),
      render: (value, data) => {
        const timeInForce = data?.time_in_force;
        const gtdExpiry = data?.gtd_expiry;
        return (
          <TradeText style={{whiteSpace: 'nowrap', justifyContent: 'flex-start', flexDirection: 'column', alignItems: 'flex-start'}}>
            <Text>{data?.limit_market === "L" ? t("limit") : t("market")}</Text>
            {/* Time In Force 표시 (지정가 주문일 때만) */}
            {data?.limit_market === 'L' && timeInForce && (
              <Text style={{ fontSize: 11, color: timeInForce === 'GTD' ? COLORS.primary : COLORS.lightGray }}>
                {timeInForce === 'GTD' && gtdExpiry
                  ? `GTD ${dayjs(gtdExpiry).format('MM/DD HH:mm')}`
                  : timeInForce}
              </Text>
            )}
          </TradeText>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: t('margin'),
      render: (value, data) => {
        const currency = data?.pairs?.split("/")[1];
        return (
          <TradeText style={{whiteSpace: 'nowrap', justifyContent: 'flex-start'}}>
            {BigNumber(data?.margin).toFormat(3)} {currency}
          </TradeText>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: t('PNL'),
      render: (value, data) => {
        const currency = data?.pairs?.split("/")[1];
        const pnlValue = BigNumber(data?.pnl_price);
        const isPositive = pnlValue.isGreaterThan(0);
        const isZero = pnlValue.isZero();
        
        return (
          <TradeText 
            style={{
              whiteSpace: 'nowrap', 
              justifyContent: 'flex-start',
              color: isZero ? '#FFFFFF' : (isPositive ? COLORS.lightGreen : COLORS.lightRed)
            }}
          >
            {pnlValue.toFormat(3)} {currency}
          </TradeText>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: t('fee'),
      render: (value, data) => {
        return (
          <TradeText style={{ justifyContent: 'flex-start', width: 'auto', minWidth: 80 }}>
            <KrwPrice
              dp={3}
              price={-BigNumber(data?.fee_price).toNumber()}
              krw={true}
              fontSize={14}
            />
          </TradeText>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: t('orderTime'),
      render: (value, data) => {
        return (
          <EntryTime style={{justifyContent: 'left'}} time={data?.created_at} />
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: t('executionType'),
      render: (value, data) => {
        const getExecutionType = (force) => {
          switch(force) {
            case 1:
              return t('forcedLiquidation');
            case 2:
              return 'TP (Take Profit)';
            case 3:
              return 'SL (Stop Loss)';
            default:
              return '-';
          }
        };
        
        return (
          <TradeText style={{whiteSpace: 'nowrap', justifyContent: 'flex-start'}}>
            {getExecutionType(data?.force)}
          </TradeText>
        );
      },
    },
  ];

  return (
    <TradeDetailListSection ref={containerRef}>
      {/*<TradeDetailListHeader dataType={dataType} />*/}
      <Modal 
        open={tradeModalStore?.tradeShareModal} 
        onCancel={()=>muateTradeModalStore({...tradeModalStore, tradeShareModal: false})} 
        footer={null}
        width={500}
        centered
      >
        <ShareModalContent/>
      </Modal>
      {isError ? <TradeDetailError /> : null}

      {token ? null : <AuthCheckText />}

      {list?.length ? (
      <Table
        columns={columns}
        dataSource={list}
        pagination={false}
        style={{ width: '100%', height: '100%' }}
        sticky={true}
        size="small"
        scroll={
          needsScroll ? {
            x: 'max-content',
            y: parentHeight - 20 - (headerHeight || 38)
          } : {
            x: 'max-content'
            }
          }
        />
      ) : (
        <EmptyMessage />
      )}

      {/*{list?.length ? <CFlatList data={list} renderItem={renderItem} /> : null}*/}
    </TradeDetailListSection>
  );
};

export default TradeFilledTable;
